// v2/layout/MobileNav.tsx — Hamburger-triggered slide-in drawer for mobile

import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  CheckSquare,
  UsersRound,
  History,
  Settings,
  Calculator,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@components/shadcn-ui/sheet';
import { Action, Role } from '../shared/types/domain';
import { useAuth } from 'context/AuthContext';
import { usePermission } from '../shared/hooks/usePermission';

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  requiredAction?: Action;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/v2/', icon: <LayoutDashboard className="h-5 w-5" /> },
  { label: 'Leads', to: '/v2/leads', icon: <Users className="h-5 w-5" /> },
  { label: 'Applications', to: '/v2/applications', icon: <FileText className="h-5 w-5" /> },
  { label: 'Tasks', to: '/v2/tasks', icon: <CheckSquare className="h-5 w-5" /> },
  { label: 'Groups', to: '/v2/groups', icon: <UsersRound className="h-5 w-5" /> },
  { label: 'EMI Calculator', to: '/v2/emi-calculator', icon: <Calculator className="h-5 w-5" /> },
  { label: 'Login History', to: '/v2/login-history', icon: <History className="h-5 w-5" />, requiredAction: Action.VIEW_USER_LOGIN_HISTORY },
  { label: 'Settings', to: '/v2/settings', icon: <Settings className="h-5 w-5" /> },
];

interface MobileNavProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function PermissionGatedMobileLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate: (to: string) => void;
}): React.ReactElement | null {
  const { userDetails } = useAuth();
  const role = (userDetails?.role as Role) ?? Role.SALES_USER;
  const isAllowed = usePermission(role, item.requiredAction!, undefined, undefined);

  if (!isAllowed) return null;
  return <MobileNavLink item={item} onNavigate={onNavigate} />;
}

function MobileNavLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate: (to: string) => void;
}): React.ReactElement {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/v2/'}
      onClick={() => onNavigate(item.to)}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors min-h-[44px] ${
          isActive
            ? 'bg-primary/10 text-primary'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
        }`
      }
    >
      {item.icon}
      <span>{item.label}</span>
    </NavLink>
  );
}

export function MobileNav({ open, onOpenChange }: MobileNavProps): React.ReactElement {
  const navigate = useNavigate();

  const handleNavigate = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-72 p-0 [&>button]:top-4 [&>button]:right-4 [&>button]:min-h-[44px] [&>button]:min-w-[44px]">
        <SheetHeader className="p-4 border-b">
          <SheetTitle className="text-left flex items-center gap-2">
            <img src="/assets/logo.png" alt="Logo" className="h-6" />
          </SheetTitle>
        </SheetHeader>
        <nav className="flex flex-col gap-1 p-3">
          {NAV_ITEMS.map((item) =>
            item.requiredAction ? (
              <PermissionGatedMobileLink key={item.to} item={item} onNavigate={handleNavigate} />
            ) : (
              <MobileNavLink key={item.to} item={item} onNavigate={handleNavigate} />
            ),
          )}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
